/**
 * WhatsAppSendHistory — persistent panel showing every past WhatsApp send
 * for a drawdown, with per-recipient status + error detail accessible after
 * the in-flight progress modal closes.
 *
 * Designed to sit below (or alongside) the existing Email "Reminder Send
 * History" / "Acknowledgment Send History" panels in the three drawdown
 * tabs. Reuses the result-modal totals styling for consistency.
 *
 * Props:
 *   drawdownId — used to load `GET /whatsapp/drawdowns/:id/jobs`
 *   refreshKey — optional bump to force a reload after a new send completes
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle, ChevronDown, ChevronRight, Loader2, RefreshCw, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLS = {
  queued:    'bg-gray-100 text-gray-600',
  sent:      'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  read:      'bg-emerald-100 text-emerald-800',
  failed:    'bg-red-100 text-red-700',
  skipped:   'bg-yellow-100 text-yellow-700',
};

const JOB_STATUS_CLS = {
  queued:     'bg-gray-100 text-gray-700',
  processing: 'bg-blue-100 text-blue-700',
  completed:  'bg-green-100 text-green-700',
  failed:     'bg-red-100 text-red-700',
  aborted:    'bg-amber-100 text-amber-700',
};

const SKIP_REASON_LABELS = {
  opted_out:           'Opted out',
  no_number:           'No number',
  unresolved_variable: 'Unresolved variable',
  no_template:         'No template',
  no_notice_pdf:       'No notice PDF',
};

const WhatsAppSendHistory = ({ drawdownId, refreshKey = 0 }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/whatsapp/drawdowns/${drawdownId}/jobs`);
      setJobs(Array.isArray(data) ? data : []);
    } catch {
      // Endpoint returns 4xx when WhatsApp is fully unset for the AMC — keep
      // the panel empty rather than rendering a noisy error. Admins can still
      // discover the issue via the in-flight progress modal.
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!drawdownId) return;
    load();
    // eslint-disable-next-line
  }, [drawdownId, refreshKey]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!loading && jobs.length === 0) {
    return null; // Don't take up real estate before the first WhatsApp send
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="dd-wa-send-history">
      <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between bg-green-50/40">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center">
          <MessageCircle size={14} className="mr-2 text-green-600" />
          WhatsApp Send History
          {jobs.length > 0 && (
            <span className="ml-2 text-xs font-normal text-gray-500">{jobs.length} job{jobs.length === 1 ? '' : 's'}</span>
          )}
        </h3>
        <button
          type="button"
          onClick={load}
          className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center"
          data-testid="dd-wa-history-refresh"
        >
          {loading ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
          Refresh
        </button>
      </div>
      <div className="divide-y divide-gray-100">
        {jobs.map((job) => {
          const totals = job.totals || {};
          const isExpanded = expanded.has(job.id);
          const date = job.created_at ? new Date(job.created_at).toLocaleString() : '';
          const jobCls = JOB_STATUS_CLS[job.status] || 'bg-gray-100 text-gray-700';
          return (
            <div key={job.id} data-testid={`dd-wa-history-job-${job.id}`}>
              <button
                type="button"
                onClick={() => toggle(job.id)}
                className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 text-left"
              >
                {isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {job.provider_template_name || '—'}
                  </div>
                  <div className="text-xs text-gray-500">{date}</div>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 text-xs">
                  <span className="text-blue-700 font-medium">{totals.sent || 0} sent</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-green-700 font-medium">{totals.delivered || 0} delivered</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-emerald-700 font-medium">{totals.read || 0} read</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-yellow-700 font-medium">{totals.skipped || 0} skipped</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-red-700 font-medium">{totals.failed || 0} failed</span>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${jobCls}`}>
                  {job.status}
                </span>
              </button>
              {isExpanded && (
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-100">
                  {(job.recipients || []).length === 0 ? (
                    <p className="text-xs text-gray-500 italic">No recipient rows captured.</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="text-gray-500 text-left">
                        <tr>
                          <th className="pb-1 pr-2">Account / Contact</th>
                          <th className="pb-1 pr-2">Phone</th>
                          <th className="pb-1 pr-2">Status</th>
                          <th className="pb-1 pr-2">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(job.recipients || []).map((r) => (
                          <tr key={r.id} className="border-t border-gray-200 align-top">
                            <td className="py-1.5 pr-2">
                              <div className="font-medium text-gray-900 truncate max-w-[14rem]">{r.account_name}</div>
                              <div className="text-gray-500">{r.contact_name}</div>
                            </td>
                            <td className="py-1.5 pr-2 font-mono text-gray-700 whitespace-nowrap">
                              {r.country_code || ''}{r.phone_number || (r.skip_reason === 'no_number' ? '—' : '')}
                            </td>
                            <td className="py-1.5 pr-2 whitespace-nowrap">
                              <span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_CLS[r.status] || STATUS_CLS.queued}`}>
                                {r.status || 'queued'}
                              </span>
                            </td>
                            <td className="py-1.5 pr-2 text-gray-700 max-w-[20rem]">
                              {r.skip_reason && (
                                <span className="block text-yellow-700">
                                  <AlertCircle size={10} className="inline mr-1" />
                                  {SKIP_REASON_LABELS[r.skip_reason] || r.skip_reason}
                                  {r.skip_detail ? ` — ${r.skip_detail}` : ''}
                                </span>
                              )}
                              {r.error_detail && (
                                <span className="block text-red-600 break-words" title={r.error_detail}>
                                  {r.error_detail}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WhatsAppSendHistory;
